/**
 * `/screen`: big screen mode (§15.8). The device connects as a screen and shows a pairing QR
 * and code; the host scans or types it from the lobby's (or the menu's) Connect big screen.
 * The screen then renders the room's public view, phase by phase (./layouts.tsx). It has no
 * controls. Its session token stays in localStorage, so a reload or a dropped connection comes
 * straight back into the room (§19: the game is not affected; the screen resumes).
 *
 * `/pair/CODE` is where the pairing QR points: scanned with a phone's camera app, it opens the
 * host's game with the Connect big screen sheet and the code filled in.
 */
import { useEffect, useRef, useState } from "react";
import { QrCode } from "../../components/QrCode.tsx";
import { lastRoom } from "../../lib/session.ts";
import { navigate } from "../../lib/router.ts";
import { readLocal, writeLocal } from "../../lib/storage.ts";
import { connectScreen, disconnect, useGame } from "../../lib/store.ts";
import { useServerNow } from "../../lib/time.ts";
import type { Announcement, PublicView } from "../../lib/types.ts";
import { joinUrl } from "../lobby/Lobby.tsx";
import { PhaseLayout, ScreenHeader } from "./layouts.tsx";

export const SCREEN_TOKEN_KEY = "handshake:screen-token";

function baseUrl(publicUrl: string | null): string {
  return (publicUrl ?? window.location.origin).replace(/\/+$/, "");
}

export function BigScreen() {
  useEffect(() => {
    connectScreen(readLocal(SCREEN_TOKEN_KEY), (token) => writeLocal(SCREEN_TOKEN_KEY, token));
    return () => disconnect();
  }, []);
  const pub = useGame((s) => s.publicView);
  const roomCode = useGame((s) => s.welcome?.roomCode ?? null);
  if (!pub || !roomCode) return <Pairing />;
  return <ScreenShell pub={pub} roomCode={roomCode} />;
}

function Pairing() {
  const code = useGame((s) => s.pairingCode);
  const status = useGame((s) => s.status);
  const publicUrl = useGame((s) => s.welcome?.publicUrl ?? null);
  const roomCode = useGame((s) => s.welcome?.roomCode ?? null);
  const link = code ? `${baseUrl(publicUrl)}/pair/${code}` : null;
  return (
    <div className="bigscreen pairing" data-testid="screen-pairing">
      <div className="screen-brand">HANDSHAKE · Big screen</div>
      {roomCode ? (
        <p className="screen-hero">Connecting to room {roomCode}…</p>
      ) : code && link ? (
        <div className="pairing-body">
          <div className="screen-qr">
            <QrCode text={link} label={`Pairing code ${code}`} />
          </div>
          <div>
            <div className="screen-label">Pairing code</div>
            <div className="screen-room-code" data-testid="pairing-code">
              {code}
            </div>
            <ol className="screen-list">
              <li>On the host's phone, open the lobby (or the menu during a game).</li>
              <li>
                Tap <strong>Connect big screen</strong>.
              </li>
              <li>Scan this code, or type it.</li>
            </ol>
            <p className="screen-faint">
              This screen shows only what every phone shows. It has no controls.
            </p>
          </div>
        </div>
      ) : (
        <p className="screen-hero candles">
          {status === "reconnecting" ? "The server is not answering yet. Retrying…" : "Connecting…"}
        </p>
      )}
    </div>
  );
}

export function ScreenShell({ pub, roomCode }: { pub: PublicView; roomCode: string }) {
  const now = useServerNow(250);
  const status = useGame((s) => s.status);
  const replay = useGame((s) => s.replay);
  const surveys = useGame((s) => (s.room?.analytics ? s.room.surveys : null));
  const publicUrl = useGame((s) => s.welcome?.publicUrl ?? null);
  const toasts = useScreenAnnouncements(pub.announcements, pub.phase.kind === "ended");
  const lastPause = [...pub.announcements].reverse().find((a) => a.kind === "paused");
  return (
    <div className="bigscreen" data-phase={pub.phase.kind} data-testid="bigscreen">
      <ScreenHeader pub={pub} now={now} />
      <main className="screen-main">
        <PhaseLayout
          pub={pub}
          now={now}
          roomCode={roomCode}
          joinUrl={joinUrl(publicUrl, roomCode)}
          replay={replay}
          surveys={surveys}
        />
      </main>
      {pub.phase.paused ? (
        <div className="screen-overlay" data-testid="screen-paused">
          <div className="screen-hero">Paused</div>
          <p className="screen-faint">{lastPause?.text ?? "The game is paused."}</p>
        </div>
      ) : null}
      <div className="screen-toasts" aria-live="polite" data-testid="screen-toasts">
        {toasts.map((t) => (
          <div key={t.id} className="screen-toast" role="status">
            {t.text}
          </div>
        ))}
      </div>
      {status === "reconnecting" || status === "connecting" ? (
        <div className="screen-conn">Reconnecting…</div>
      ) : null}
    </div>
  );
}

/**
 * New public announcements (timer changes, pauses, host changes) as large toasts: at most two
 * at a time, the same text once, each for 8 seconds. None once the game has ended.
 */
function useScreenAnnouncements(
  announcements: readonly Announcement[],
  ended: boolean,
): { id: number; text: string }[] {
  const [toasts, setToasts] = useState<{ id: number; text: string }[]>([]);
  const seen = useRef<number | null>(null);
  const nextId = useRef(1);
  useEffect(() => {
    const latest = announcements.reduce((m, a) => Math.max(m, a.at), 0);
    if (seen.current === null) {
      seen.current = latest;
      return;
    }
    const fresh = announcements.filter((a) => a.at > (seen.current ?? 0));
    seen.current = Math.max(seen.current, latest);
    if (fresh.length === 0 || ended) return;
    const added = fresh.map((a) => ({ id: nextId.current++, text: a.text }));
    setToasts((t) => {
      const texts = new Set(added.map((a) => a.text));
      return [...t.filter((x) => !texts.has(x.text)), ...added].slice(-2);
    });
    const ids = new Set(added.map((a) => a.id));
    setTimeout(() => setToasts((t) => t.filter((x) => !ids.has(x.id))), 8_000);
  }, [announcements, ended]);
  return ended ? [] : toasts;
}

/** `/pair/CODE`: hands the code to the host's game on this phone. */
export function PairRedirect({ code }: { code: string }) {
  const room = lastRoom();
  useEffect(() => {
    if (room) navigate(`/play/${room}?pair=${encodeURIComponent(code)}`, true);
  }, [room, code]);
  if (room) return null;
  return (
    <div className="app">
      <main className="main no-tabs stack center">
        <h1>Big screen {code}</h1>
        <p className="muted">
          To connect this big screen, open your room on this phone as its host, then tap Connect big
          screen and type {code}.
        </p>
        <a href="/">Back to the start</a>
      </main>
    </div>
  );
}
